<template>
	<div
		class="app-dropdown"
		:class="{
			'app-dropdown--disabled' : $attrs.disabled,
			'app-dropdown--error' : hasError
		}"
	>
		<div
			v-if="$slots.default"
			class="app-dropdown__label"
		>
			<span
				v-if="$attrs.required"
				class="require-mark"
			>*</span>
			<slot></slot>
		</div>

		<div class="app-dropdown__wrapper">
			<div
				class="app-dropdown__toggle"
			>
				<input
					type="text"
					class="app-dropdown__hidden"
					:value="hasValue ? 1 : ''"
					:required="$attrs.required"
					@invalid="setInvalidMessage"
				>

				<div class="app-dropdown__selected" :class="{'vertical' : position === 'vertical'}">
					<template>
                        <div
                            v-if="selected.length < 1"
                            class="app-dropdown__placeholder"
                            :class="{'app-dropdown__placeholder--empty' : !$attrs.placeholder}"
                            v-text="$attrs.placeholder || ' '"
                        />

						<div
							v-for="(option, index) in selected"
							:key="index"
						>
							<slot name="selected" :props="{option, remove}">
								<span
									class="app-dropdown__value app-dropdown__value--tag"
								>
									{{ option[displayProp] || option }}

									<span
										class="app-dropdown__remove"
										@click.stop="remove(option)"
									>
										&times;
									</span>
								</span>
							</slot>
						</div>

                        <input
                            class="app-dropdown__input"
                            type="text"
                            v-model="search"
                            @keypress="enter"
                        >
					</template>
				</div>
			</div>
		</div>

		<p
			v-if="hasError"
			class="app-dropdown__error"
		>
			{{ invalidMessage || error}}
		</p>
	</div>
</template>

<script>
export default {
	props: {
		displayProp: String,
		error: String,
		position: String
	},

	data () {
		return {
			search: '',
			invalidMessage: null
		}
	},

	computed: {
		selected () {
			if (this.$attrs.value) return this.$attrs.value
			return []
		},

		hasError () {
			return this.error || this.invalidMessage
		},

		hasValue () {
			if (this.selected instanceof Array && this.selected.length === 0) return false
			if (this.selected.constructor === Object && Object.keys(this.selected).length === 0) return false
			return !!this.selected
		}
	},

	methods: {
		enter (e) {
			if (e.keyCode === 13 || e.keyCode === 32) {
				e.preventDefault()
				if (!this.search) return

				const newVal = [...this.selected]
				newVal.push(this.search)
				this.search = ''
				this.$emit('input', newVal)
			}
		},

		remove (option) {
			const newVal = this.selected.filter(item => {
				return JSON.stringify(item) !== JSON.stringify(option)
			})
			this.$emit('input', newVal)
		},

		setInvalidMessage (event) {
			event.preventDefault()
			if (event.target.validity.valueMissing) this.invalidMessage = `Fill up this field`
		}
	}
}
</script>

<style lang="scss" scoped>
.app-dropdown {
	outline: none;
	display: inline-block;

	&__wrapper {
		width: 100%;
		position: relative;
		color: $default;
	}

	&__hidden {
		position: absolute;
		bottom: 0;
		left: 0;
		width: 100%;
		pointer-events: none;
		z-index: 0;
		opacity: 0;
	}

	&__label {
		margin-bottom: .5em;

		.require-mark {
			color: $danger;
		}
	}

	&__toggle {
		display: flex;
		align-items: center;
		position: relative;
		width: 100%;
		border-radius: 3px;
		border: 1px solid $default-light;
		background-color: #fff;
		user-select: none;
		// cursor: pointer;
	}

	&__arrow {
		flex-grow: 0;
		width: .5em;
		height: .5em;
		border: solid $default-light;
		border-width: 0 .1em .1em 0;
		transform: rotate(45deg);
		pointer-events: none;
		margin: 0 8px;

		&--up { transform: rotate(-135deg); }
	}

	&__placeholder, &__selected {
		flex: 1 1 auto;
		font-size: 1em;
	}

	&__placeholder {
		color: $default-light;
		padding: 5px 8px;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;

		&--empty { white-space: pre-wrap; }
	}

	&__selected {
		display: flex;
		overflow-x: hidden;

        &.vertical {
            flex-wrap: wrap;
            overflow-y: auto;
            max-height: 65px;
        }
	}

	&__toggle:hover &__selected {
		overflow-x: auto;
	}

	&__selected > * {
		flex-grow: 0;
		flex-shrink: 0;
		margin: 4px;
	}

    &__input {
        border: 0;
    }

	&__value {
		padding: 1px 3px;
		border-radius: 3px;
		display: inline-block;

		&--tag {
			background-color: $primary-light;
			color: $primary;
		}

	}

	&__remove {
		pointer-events: all;
		display: inline-block;
		color: $primary;
		cursor: pointer;
	}

	&__options-wrapper {
		position: absolute;
		top: 100%;
		left: 0;
		width: auto;
		min-width: 100%;
		margin: 5px 0;
		border: 1px solid $default-light;
		border-radius: 3px;
		background-color: #fff;
		z-index: 999;

		&--top {
			top: auto;
			bottom: 100%;
		}

		&--right {
			left: auto;
			right: 0;
		}
	}

	&__options {
		list-style: none;
		width: 100%;
		max-height: 150px;
		overflow-x: hidden;
		overflow-y: auto;
	}

	&__option,
	&__empty {
		padding: 5px 8px;
		user-select: none;
	}

	&__option {
		cursor: pointer;
		display: flex;
		align-items: center;
		line-height: 1em;

		&--selected {
			color: $primary;
		}

		&:hover {
			background-color: $primary;
			color: #fff;
		}

		> *:not(:first-child) {
			margin-left: 8px;
		}
	}

	&__search {
		display: flex;
		align-items: center;
		padding: 5px 0;
		margin: 5px 8px;
		border: solid $default-light;
		border-width: 0 0 .5px 0;

		input {
			flex: 1 1 auto;
			font-size: 1em;
			padding-right: 8px;
			outline: none;
			border: none;

			&::placeholder {
				color: $default-light;
			}
		}

		.icon {
			width: 1em;
			height: 1em;
		}
	}

	&:hover &__toggle,
	&:hover &__options-wrapper {
		border-color: $primary;
	}

	&--disabled { pointer-events: none; }

	&--disabled &__toggle {
		opacity: 0.5;
	}

	&--error &__toggle,
	&--error:hover &__toggle {
		border-color: $danger;
	}

	&__error {
		text-align: right;
		color: $danger;
		margin-top: .5em;
	}
}
</style>



// WEBPACK FOOTER //
// src/components/app-components/AppMultipleTextField.vue